import { useState, useEffect, useMemo, useContext } from "react";
import SearchBar from "./SearchBar";
import { ViewContext } from 'components/lib';
import axios from 'axios';
import Styles from './DIYAssessment.module.scss'
import { useNavigate, useParams } from 'react-router-dom';
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { KeyboardArrowDown, Quiz, BarChart, Assignment, GroupAdd, Refresh } from "@mui/icons-material";
import {
    Select,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Pagination,
    Divider,
    ListItemIcon,
    CircularProgress,
    Typography,
    Box,
    Skeleton,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Chip
} from '@mui/material';
import InviteUsersModal from "./InviteUsersModal";
import { AssessmentMenu } from "./AssessmentCardMenu";

const AssessmentCard = ({ assessment, onInvite, onRefresh, onViewReport, onEdit, onArchive, onClone, onTestModeToggle, isStudent = false }) => {
    let assessmentIcon = null;
    switch (assessment.type) {
        case 'case-study':
            assessmentIcon = <Assignment fontSize="small" />;
            break;
        case 'quiz':
            assessmentIcon = <Quiz fontSize="small" />;
            break;
        default:
            assessmentIcon = <Assignment fontSize="small" />;
            break;
    }
    const getIndicatorColor = (status) => {
        switch (status) {
            case 'draft':
                return 'darkgrey';
            case 'archived':
                return 'purple';
            case 'published':
                return '#1450a0'
            case 'closed':
                return '#FF1616'
            case 'live':
                return '#008037'
            default:
                return 'darkgrey'
        }
    }



    return (
        <div className={Styles.assessmentCardWrapper}>
            <div className={`${Styles.assessmentCard} ${assessment.is_test_mode ? Styles.assessmentCardTestMode : ''}`}>
                {!!assessment.is_test_mode && <span className={Styles.testModeBadge}>Test Mode</span>}
                <div className={Styles.nameStatusContainer}>
                    <span className={Styles.assessmentNameContainer}>
                        {assessmentIcon}
                        <span title={assessment.name} className={Styles.assessmentName}>
                            {assessment.name}
                        </span>
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Chip label={assessment.status} size="small" sx={{ backgroundColor: getIndicatorColor(assessment.status), color: 'white', minWidth: '80px' }} />
                    </span>
                </div>
                {!isStudent && (
                    <Tooltip
                        title={
                            <Box p={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
                                <Typography variant="body2"><strong>Total Registrations:</strong> {assessment.total || 0}</Typography>
                                <Typography variant="body2"><strong>Total Submissions:</strong> {assessment.completed || 0}</Typography>
                                <Typography variant="body2"><strong>Total Evaluations:</strong> {assessment.evaluated || 0}</Typography>
                                <Typography variant="body2"><strong>Submission %:</strong>
                                    {assessment.total
                                        ? (((assessment.completed || 0) / assessment.total) * 100).toFixed(1)
                                        : 0}%
                                </Typography>
                                <Typography variant="body2"><strong>Evaluation %:</strong>
                                    {assessment.total
                                        ? (((assessment.evaluated || 0) / assessment.total) * 100).toFixed(1)
                                        : 0}%
                                </Typography>
                            </Box>
                        }
                        arrow
                        enterTouchDelay={0} // Show tooltip on click (mobile-friendly)
                    >
                        <div className={Styles.statsSection}>
                            <Box className={Styles.statItem} display="flex" flexDirection="column" alignItems="center">
                                <Typography variant="h6" fontWeight="bold" color="#1450a0">
                                    {assessment.total || 0}
                                </Typography>
                                <Typography variant="body2" color="#1450a0">
                                    Registrations
                                </Typography>
                            </Box>

                            <Box className={Styles.statItem} display="flex" flexDirection="column" alignItems="center">
                                <Typography variant="h6" fontWeight="bold" color="#E0A800">
                                    {assessment.completed || 0}
                                </Typography>
                                <Typography variant="body2" color="#E0A800">
                                    Submissions
                                </Typography>
                            </Box>

                            <Box className={Styles.statItem} display="flex" flexDirection="column" alignItems="center">
                                <Typography variant="h6" fontWeight="bold" color="#008037">
                                    {assessment.evaluated || 0}
                                </Typography>
                                <Typography variant="body2" color="#008037">
                                    Evaluations
                                </Typography>
                            </Box>
                        </div>
                    </Tooltip>
                )}
                <div className={Styles.actionSection}>
                    <Tooltip title="View Reports" placement="top">
                        <span>
                            <IconButton
                                onClick={() => onViewReport(assessment.id)}
                                disabled={['draft', 'archived'].includes(assessment.status)}
                                sx={{
                                    '&:hover': {
                                        color: '#1450a0',
                                        backgroundColor: 'rgba(20, 80, 160, 0.1)', // Light blue background on hover
                                    },
                                    transition: 'color 0.3s ease',
                                }}
                            >
                                <BarChart />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Invite Users" placement="top">
                        <span>
                            <IconButton
                                onClick={() => onInvite(assessment)}
                                disabled={['draft', 'archived'].includes(assessment.status)}
                                sx={{
                                    '&:hover': {
                                        color: '#1450a0',
                                        backgroundColor: 'rgba(20, 80, 160, 0.1)', // Light blue background on hover
                                    },
                                    transition: 'color 0.3s ease',
                                }}
                            >
                                <GroupAdd />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Refresh Stats" placement="top">
                        <span>
                            <IconButton
                                onClick={() => onRefresh(assessment.id)}
                                disabled={['draft', 'archived'].includes(assessment.status)}
                                sx={{
                                    '&:hover': {
                                        color: '#1450a0',
                                        backgroundColor: 'rgba(20, 80, 160, 0.1)', // Light blue background on hover
                                    },
                                    transition: 'color 0.3s ease',
                                }}
                            >
                                {assessment.isRefreshing ? (
                                    <CircularProgress size={16} thickness={4} />
                                ) : (
                                    <Refresh fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <div style={{ marginLeft: '0.2em' }}>
                        <AssessmentMenu
                            assessment={assessment}
                            onEdit={() => { onEdit(assessment.id) }}
                            onArchive={() => { onArchive(assessment.id) }}
                            onClone={() => { onClone(assessment.id) }}
                            onTestModeToggle={onTestModeToggle}
                        />
                    </div>
                </div>
            </div>
        </div >
    );
};

export const DiyAssessment = () => {
    const viewContext = useContext(ViewContext);
    const navigate = useNavigate();
    const { diyQuizId } = useParams();

    const [assessments, setAssessments] = useState([]);
    const [diyQuizName, setDiyQuizName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [sortBy, setSortBy] = useState({
        sortBy: 'created_at',
        sortOrder: 'desc',
    });
    const [paginationData, setPaginationData] = useState(null)
    const [pageNumber, setPageNumber] = useState(1)
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [inviteAssessment, setInviteAssessment] = useState(null)
    const [anchorEl, setAnchorEl] = useState(null);
    const [tabValue, setTabValue] = useState("open");
    const open = Boolean(anchorEl);

    const sortOptions = useMemo(() => [
        { label: 'Newest First', value: { sortBy: 'created_at', sortOrder: 'desc' } },
        { label: 'Oldest First', value: { sortBy: 'created_at', sortOrder: 'asc' } },
        { label: 'Name (A-Z)', value: { sortBy: 'name', sortOrder: 'asc' } },
        { label: 'Name (Z-A)', value: { sortBy: 'name', sortOrder: 'desc' } },
    ], []);

    const fetchData = async () => {
        setLoading(true)
        try {
            const statusFilter = tabValue === "archived" ? "archived" : "open";
            const pageURL = `/api/diy-quiz/${diyQuizId}/assessments?pageNumber=${pageNumber}&sortBy=${sortBy.sortBy}&sortOrder=${sortBy.sortOrder}&status=${statusFilter}`
            const response = await axios.get(pageURL);
            setPaginationData(response.data?.pagination)
            setAssessments(response.data?.data)
            setDiyQuizName(response.data?.quizName)
            setLoading(false)
        } catch (error) {
            console.error('Error fetching data:', error);
            viewContext.notification.show('Error Fetching Assessments', 'error', true);
            setLoading(false)
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, sortBy.sortBy, sortBy.sortOrder, tabValue])

    const handleInvite = (assessment) => {
        setIsInviteModalOpen(true);
        setInviteAssessment(assessment)
    };

    const handleViewReport = (assessmentId) => {
        navigate(`/diy-quiz/${diyQuizId}/assessments/${assessmentId}/reports`)
        return;
    }

    const handleEdit = (assessmentId) => {
        navigate(`/diy-quiz/${diyQuizId}/assessments/${assessmentId}/edit`)
    }

    const handleArchive = (assessmentId) => {
        const data = {
            status: 'archived'
        }
        axios.patch(
            `/api/diy-quiz/${diyQuizId}/assessments/${assessmentId}`,
            data,
            {
                headers: {
                    accept: 'application/json',
                }
            }
        )
            .then(response => {
                if (response.data) {
                    setAssessments(prevAssessments =>
                        prevAssessments.filter(assessment =>
                            assessment.id !== assessmentId
                        )
                    );
                    viewContext.notification.show('Successfully archived assessment', 'success', true);
                } else {
                    viewContext.notification.show('Error archiving assessment', 'error', true);
                }

            })
            .catch(error => {
                console.error('Error:', error);
                viewContext.notification.show('Error archiving assessment', 'error', true);
            });
    }

    const handleClone = (assessmentId) => {
        axios.put(
            `/api/diy-quiz/${diyQuizId}/assessments/${assessmentId}`,
            {},
            {
                headers: {
                    accept: 'application/json',
                }
            }
        )
            .then(response => {
                const clonedAssessment = response.data?.data;

                if (clonedAssessment) {
                    setAssessments(prevAssessments => [clonedAssessment, ...prevAssessments,]);
                    viewContext.notification.show('Successfully cloned assessment', 'success', true);
                } else {
                    viewContext.notification.show('Error cloning assessment', 'error', true);
                }
            })
            .catch(error => {
                console.error('Error:', error);
                viewContext.notification.show('Error cloning assessment', 'error', true);
            });
    }

    const handleTestModeToggle = (value, assessmentId) => {

        const data = {
            is_test_mode: !!value
        }
        axios.patch(
            `/api/diy-quiz/${diyQuizId}/assessments/${assessmentId}`,
            data,
            {
                headers: {
                    accept: 'application/json',
                }
            }
        )
            .then(response => {
                if (response.data) {
                    setAssessments(prevAssessments =>
                        prevAssessments.map(assessment =>
                            assessment.id === assessmentId
                                ? { ...assessment, is_test_mode: value }
                                : assessment
                        )
                    );
                    const message = !!value ? 'Turned on Test Mode' : 'Turned Off Test Mode'
                    viewContext.notification.show(message, 'success', true);
                } else {
                    viewContext.notification.show('Error updating Test Mode', 'error', true);
                }
            })
            .catch(error => {
                console.error('Error:', error);
                viewContext.notification.show('Error updating Test Mode', 'error', true);
            });
    }

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (option) => {
        switch (option) {
            case 'case-study':
                navigate(`/diy-quiz/${diyQuizId}/assessments/create?type=case-study`);
                break;
            case 'quiz':
                navigate(`/diy-quiz/${diyQuizId}/assessments/create?type=quiz`);
                break;
            default:
                navigate(`/diy-quiz/${diyQuizId}/assessments/create?type=quiz`);
                break;
        }
        handleClose();
    };

    const handlePageChange = (event, value) => {
        setPageNumber(value);
        setSearchQuery('');
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        const pageUrl = query
            ? `/api/diy-quiz/${diyQuizId}/assessments?search=${query}`
            : `/api/diy-quiz/${diyQuizId}/assessments?pageNumber=${pageNumber}&sortBy=${sortBy.sortBy}&sortOrder=${sortBy.sortOrder}`;
        axios.get(pageUrl)
            .then(response => {
                setAssessments(response.data.data)
            })
            .catch(error => {
                console.error('Error:', error);
                viewContext.notification.show(error.message, 'error');
            });
    }

    const handleRefresh = (assessmentId) => {
        setAssessments(prevAssessments =>
            prevAssessments.map(
                assessment => assessment.id === assessmentId
                    ? { ...assessment, isRefreshing: true }
                    : assessment
            )
        );

        axios.get(`/api/diy-quiz/${diyQuizId}/assessments/${assessmentId}/refresh`)
            .then(response => {
                if (response.data) {
                    // Update just this assessment in the state
                    setAssessments(prevAssessments =>
                        prevAssessments.map(assessment =>
                            assessment.id === assessmentId
                                ? { ...response.data?.data, isRefreshing: false }
                                : assessment
                        )
                    );
                    viewContext.notification.show('Assessment data refreshed', 'success', true);
                }
            })
            .catch(error => {
                console.error('Error refreshing assessment:', error);
                viewContext.notification.show('Error refreshing assessment', 'error', true);
                // Reset the loading state
                setAssessments(prevAssessments =>
                    prevAssessments.map(assessment =>
                        assessment.id === assessmentId
                            ? { ...assessment, isRefreshing: false }
                            : assessment
                    )
                );
            });

    }

    return (
        <div id='ancestorConatiner' className={Styles.ancestorContainer}>
            <div className={Styles.headerContainer}>
                <BreadCrumbs
                    onClickPaths={['/diy-quiz', '']}
                    crumbs={["Your DIY quizzes", `${diyQuizName}:Assessments`]}
                />
                <div>
                    <AivagamButton
                        buttonText={
                            <span className={Styles.addButton}>
                                Add <KeyboardArrowDown fontSize="small" />
                            </span>
                        }
                        onClick={handleClick}
                        classStyles={{
                            width: '7em',
                            height: '3em',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                        }}
                    />
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        {/*Uncomment the following when quiz is implemented */}
                        {/* <MenuItem onClick={() => handleMenuItemClick('quiz')}>
                                    <ListItemIcon>
                                        <Quiz fontSize="small" sx={{ mr: '0.7em' }} />
                                        Quiz
                                    </ListItemIcon>
                                </MenuItem> */}
                        {/* <Divider /> */}
                        <MenuItem onClick={() => handleMenuItemClick('case-study')}>
                            <ListItemIcon>
                                <Assignment fontSize="small" sx={{ mr: '0.7em' }} />
                                Case Study
                            </ListItemIcon>
                        </MenuItem>
                    </Menu>
                </div>
            </div>
            <Tabs value={tabValue} onChange={(event, newValue) => setTabValue(newValue)}>
                <Tab label="Open" value="open" />
                <Tab label="Archived" value="archived" />
            </Tabs>
            <div className={Styles.filterContainer}>
                <SearchBar onSearch={handleSearch} disabled={false} />
                <FormControl size="small" sx={{ width: '150px' }}>
                    <InputLabel>Sort by</InputLabel>
                    <Select
                        value={JSON.stringify(sortBy)}
                        onChange={(e) => setSortBy(JSON.parse(e.target.value))}
                        label="Sort by"
                        sx={{
                            '& .Mui-disabled': {
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed'
                            }
                        }}
                    >
                        {sortOptions.map((option) => (
                            <MenuItem key={option.label} value={JSON.stringify(option.value)}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            {loading ? (
                <div className={Styles.assessmentList}>
                    {[1, 2, 3, 4].map((index) => (
                        <Skeleton
                            key={index}
                            variant="rectangle"
                            width="100%"
                            height={45}
                        />
                    ))}
                </div>
            ) : (
                <>
                    <div id='assessmentList' className={Styles.assessmentList}>
                        {assessments?.length > 0 ? (
                            assessments.map(assessment => (
                                <AssessmentCard
                                    key={assessment.id}
                                    assessment={assessment}
                                    onInvite={handleInvite}
                                    onRefresh={handleRefresh}
                                    onViewReport={handleViewReport}
                                    onEdit={handleEdit}
                                    onArchive={handleArchive}
                                    onClone={handleClone}
                                    onTestModeToggle={handleTestModeToggle}
                                />
                            ))
                        ) : (
                            searchQuery ? (
                                <div className={Styles.noResults}>
                                    <p>No assessments found matching your search.</p>
                                    <p>Try different keywords or clear your search to see all assessments.</p>
                                </div>
                            ) : (
                                <div className={Styles.noResults}>
                                    <p>You haven't created any assessments yet.</p>
                                    <p>Click the "Add" button above to create your first assessment.</p>
                                </div>
                            )
                        )}
                    </div>
                </>
            )}
            {/* Pagination */}
            <div className={Styles.paginationContainer}>
                <Pagination
                    color="primary"
                    page={pageNumber}
                    count={paginationData?.totalPages || 1}
                    size="small"
                    sx={{
                        maxWidth: "50%",
                        width: "fit-content",
                        marginTop: "0.5em",
                    }}
                    onChange={handlePageChange}
                />
            </div>
            <InviteUsersModal
                open={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
                assessment={inviteAssessment || null}
                diyQuizId={diyQuizId}
                uploadUrl={'/api/diy/invite'}
                setAssessments={setAssessments}
            />
        </div>
    )
}